import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiLinkExternal, BiPlus, BiTrash, BiX } from 'react-icons/bi';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import PopoverWithAudio from '../../components/common/PopoverWithAudio';
import TextScenarioLinkTypeComponent from '../../components/dashboard/knowledge-bases-create/scenario/text-scenario/text-scenario-link-type-component';
import { wizardState } from './botSlice';

type ICloneExternalLinks = {
  initialData:
    | Array<{
        logo: string;
        link: string;
      }>
    | [];
  onChange: (
    externalLinks:
      | Array<{
          logo: string;
          link: string;
        }>
      | []
  ) => any;
};

const prefixLinkList = ['https://wa.me/', 'mailto:', 'tel:', 'https://'];

const popupAudioPath: { [key: string]: string } = {
  en: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716230449_add_clickable_link_en.mp3',
  fr: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716230438_add_clickable_link_fr.mp3',
};

const CloneExternalLinks = ({ initialData, onChange }: ICloneExternalLinks) => {
  const { t, i18n } = useTranslation();

  const wizard = useAppSelector(wizardState);

  const permissions = useAppSelector(state => (state.dashboardState as any).Client.botConfigs).find((item: any) => item.botName === wizard.bot_code)?.config;

  const schema = Yup.object({
    logo: Yup.string().required().label(t('CloneExternalLinks/key1')),
    link: Yup.string().required().label(t('CloneExternalLinks/key2')),
    // link: Yup.string().test({
    //   name: "isValidLink",
    //   message: "Invalid link. Please enter a valid URL or phone number.",
    //   test: (value) => {
    //     const allowedPrefixes = [
    //       "https://wa.me/",
    //       "mailto:",
    //       "tel:",
    //       "https://"
    //     ];

    //     const prefix = allowedPrefixes.find((prefix) =>
    //       value?.startsWith(prefix)
    //     );

    //     if (!prefix) {
    //       return false;
    //     }

    //     if (prefix === "mailto:") {
    //       // Validate as email address
    //       return Yup.string()
    //         .email()
    //         .isValidSync(value?.substring(prefix.length));
    //     }

    //     if (prefix === "tel:") {
    //       // Validate as phone number
    //       return Yup.string()
    //         .matches(/^\d+$/)
    //         .isValidSync(value?.substring(prefix.length));
    //     }

    //     // Validate as URL
    //     return Yup.string().url().isValidSync(value);
    //   }
    // }),
    prefix: Yup.string()
      .oneOf(prefixLinkList, t('Invalid prefix')) // Validation rule for prefix
      .required(t('Prefix is required')),
  });

  const { register, formState, reset, watch, getValues, setValue, handleSubmit } = useForm<{ logo: string; link: string; prefix: string }>({
    defaultValues: {
      logo: 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg',
      link: '',
      prefix: 'https://',
    },
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
  });

  const [internalExternals, setInternalExternals] = useState<
    Array<{
      logo: string;
      link: string;
    }>
  >(initialData ?? []);
  const [show, setShow] = useState(false);
  const [targetItemIndex, setTargetItemIndex] = useState<number>();
  const [placeHolder, setPlaceHolder] = useState('');

  const splitUrl = (url: string) => {
    for (const prefix of prefixLinkList) {
      if (url.startsWith(prefix)) {
        return {
          prefix: prefix,
          remaining: url.slice(prefix.length),
        };
      }
    }
    return null; // Return null if no prefix matches
  };

  const onSubmit = (data: { logo: string; link: string; prefix: string }) => {
    if (targetItemIndex! >= 0) {
      const newExternalLinks = [...internalExternals];
      newExternalLinks[targetItemIndex!] = {
        logo: data.logo,
        link: data.prefix + (data.link.startsWith('https://') ? data.link.replace('https://', '') : data.link),
      };
      setInternalExternals(newExternalLinks);
      onChange(newExternalLinks);
      setShow(false);
      reset();
    } else {
      const newExternalLinks = [
        ...internalExternals,
        {
          logo: data.logo,
          link: data.prefix + (data.link.startsWith('https://') ? data.link.replace('https://', '') : data.link),
        },
      ];
      setInternalExternals(newExternalLinks);
      onChange(newExternalLinks);
      setShow(false);
      reset();
    }
  };

  useEffect(() => {
    if (!show) setPlaceHolder('');

    return () => {
      setPlaceHolder('');
    };
  }, [show]);

  return (
    <div className="tw-h-10 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
      {internalExternals.map((externalLink, index) => (
        <button
          id="btn-clone-link"
          type="button"
          key={index}
          className="tw-p-[11px] tw-bg-white tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-200 tw-justify-start tw-items-center tw-gap-2.5 tw-flex"
          onClick={() => {
            setTargetItemIndex(index);
            reset({
              link: splitUrl(externalLink.link)?.remaining,
              logo: externalLink.logo,
              prefix: splitUrl(externalLink.link)?.prefix ?? 'https://',
            });

            setShow(true);
          }}
        >
          <div className="tw-w-[18px] tw-h-[18px] tw-justify-center tw-items-center tw-flex">
            <img className="tw-w-[18px] tw-h-[18px]" alt="" src={externalLink.logo} />
          </div>
        </button>
      ))}
      <PSButton
        id="btn-clone-link"
        type="button"
        className="gap-2"
        onClick={() => {
          setTargetItemIndex(undefined);
          setShow(true);
          reset({
            logo: 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg',
            link: '',
            prefix: 'https://',
          });
        }}
        icon={<BiPlus size={22} className="tw-text-white" />}
        title={t('CloneExternalLinks/key3')}
      />

      <Modal centered show={show} size="lg">
        <Form onSubmit={e => e.preventDefault()}>
          <Modal.Header className="tw-p-3 tw-self-stretch  tw-rounded-tl-2xl tw-rounded-tr-2xl tw-justify-between tw-items-center tw-flex tw-w-full tw-border-0">
            <div className="tw-flex tw-flex-row tw-justify-between tw-w-full">
              <div className="tw-flex tw-items-center">
                <div className="tw-h-11 tw-pl-2.5 tw-pr-4 tw-py-2.5 tw-bg-gray-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2 tw-inline-flex">
                  <BiLinkExternal className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
                  <div className="tw-text-gray-800 tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight">{t('MacBotLinkForm/title')}</div>
                </div>

                <PopoverWithAudio
                  data={{
                    size: 20,
                    contentText: t('MacBotOverrideVariableForm/Tooltips'),
                  }}
                />
              </div>
              <button
                id="btn-clone-x"
                onClick={() => {
                  reset();
                  setShow(false);
                }}
                className="tw-cursor-pointer tw-p-2 tw-bg-gray-100 tw-rounded-[20px] tw-justify-start tw-items-center tw-gap-3 tw-flex tw-border-none tw-text-gray-800"
                type="button"
              >
                <BiX className="tw-w-6 tw-h-6 tw-relative" />
              </button>
            </div>
          </Modal.Header>
          <Modal.Body>
            <Row className="tw-items-start">
              <Col lg={2} xs={12} className="px-3">
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">Icon</Form.Label>
                <TextScenarioLinkTypeComponent
                  value={getValues('logo') ? getValues('logo') : 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg'}
                  onChange={({ url, prefix, placeHolder }: { url: string; prefix: string; placeHolder: string }) => {
                    setValue('link', '');

                    if (url) {
                      setValue('logo', url);
                      setValue('prefix', prefix);
                      setPlaceHolder(placeHolder);
                    } else {
                      setValue('logo', 'https://cdn.1cdn.app/application/LOUISEBOT/2409021316194_bx-link-external.svg');
                    }
                  }}
                />
              </Col>
              <Col lg={10} xs={12} className="px-3">
                <Form.Group>
                  <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t('CloneExternalLinks/key4')}
                  </Form.Label>

                  <div className="tw-flex ">
                    <div className="tw-flex-1 form-control tw-border-e-0 tw-rounded-e-none  tw-py-5 tw-px-3 tw-bg-gray-50 tw-rounded-tl-md tw-rounded-bl-md tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex">
                      <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{watch('prefix') ?? 'https://'}</div>
                    </div>
                    <Form.Control
                      className="tw-flex-[10] tw-border-s-0 tw-rounded-s-none focus:tw-border-none diInput  focus:tw-shadow-black focus:tw-border-transparent"
                      type={watch('prefix') === 'mailto:' ? 'email' : watch('prefix') === 'tel:' ? 'number' : 'url'}
                      {...register('link')}
                      placeholder={placeHolder}
                      onFocus={e => (e.target.placeholder = '')}
                      onBlur={e => (e.target.placeholder = placeHolder)}
                    />
                  </div>

                  <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                    {formState.errors.link?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className={`tw-border-t-0 ${targetItemIndex! >= 0 ? 'tw-justify-between' : 'tw-justify-end'}`}>
            {targetItemIndex! >= 0 && (
              <div className="tw-w-6 tw-h-4 tw-shrink-0 tw-rounded-full tw-bg-slate-300 tw-p-2 tw-flex tw-flex-col tw-justify-center tw-items-center">
                <button
                  id="btn-clone-close"
                  className="tw-text-neutral-800 tw-rounded-full tw-border-none tw-p-2 tw-text-lg cursor-pointer"
                  onClick={() => {
                    const newExternalLinks = internalExternals.filter((_, i) => i !== targetItemIndex);
                    setInternalExternals(newExternalLinks);
                    onChange(newExternalLinks);
                    setTargetItemIndex(undefined);
                    setShow(false);
                  }}
                  type="button"
                >
                  <BiTrash size={24} className="tw-text-gray-800 tw-text-xl" />
                </button>
              </div>
            )}
            <PSButton
              id="btn-clone-submit"
              type="button"
              onClick={handleSubmit(onSubmit)}
              className="tw-border-none tw-h-10 tw-px-[17px] tw-py-2.5 disabled:tw-bg-gray-300 tw-bg-primary tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              title={t('MacBotUserInputForm/btn-01')}
            />
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default WrappedErrorBoundary(CloneExternalLinks);
